var __extends = (this && this.__extends) || (function () {
    var extendStatics = function (d, b) {
        extendStatics = Object.setPrototypeOf ||
            ({ __proto__: [] } instanceof Array && function (d, b) { d.__proto__ = b; }) ||
            function (d, b) { for (var p in b) if (b.hasOwnProperty(p)) d[p] = b[p]; };
        return extendStatics(d, b);
    };
    return function (d, b) {
        extendStatics(d, b);
        function __() { this.constructor = d; }
        d.prototype = b === null ? Object.create(b) : (__.prototype = b.prototype, new __());
    };
})();
var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
/**
 * @desc slider
 */
import { createRef } from '@byted/light-render';
import * as lodash from '../../utils/lodash';
import { Group, Text, Rect, Polyline, CompoundPath } from '../../element';
import { ComponentModel } from '../../model';
import { LinearScale } from '../../scale';
import { getPercentOrNumberValue, isPercent } from '../../utils/math';
import { createSymbolAtCenter } from '../../symbol';
import measureText from '../../utils/measureText';
import { registerComponent } from '../index';
import { getSymbolSize } from '../../utils/styleHelper';
var handleIconPath = 'M4 0H3V5.03544C1.30385 5.27806 0 6.73676 0 8.5V15.5C0 17.2632 1.30385 18.7219 3 18.9646V24H4V18.9646C5.69615 18.7219 7 17.2632 7 15.5V8.5C7 6.73676 5.69615 5.27806 4 5.03544V0ZM2 10.5C2 10.2239 2.22386 10 2.5 10H4.5C4.77614 10 5 10.2239 5 10.5C5 10.7761 4.77614 11 4.5 11H2.5C2.22386 11 2 10.7761 2 10.5ZM2 13C2 12.7239 2.22386 12.5 2.5 12.5H4.5C4.77614 12.5 5 12.7239 5 13C5 13.2761 4.77614 13.5 4.5 13.5H2.5C2.22386 13.5 2 13.2761 2 13Z';
// const handleHeight = 28;
/**
 * slider
 */
var Slider = /** @class */ (function (_super) {
    __extends(Slider, _super);
    function Slider() {
        var _this = _super !== null && _super.apply(this, arguments) || this;
        _this.type = 'slider';
        _this._startHandleRef = createRef();
        _this._endHandleRef = createRef();
        _this._detailText = [
            new Text({ key: 'slider-start' }),
            new Text({ key: 'slider-end' }),
        ];
        _this.handleSideDragEnd = function () {
            if (!_this.state.isDragging) {
                return;
            }
            _this.chart.setInteraction(false);
            _this.setState({
                isDragging: false,
                draggingSide: null,
            });
            _this.renderDetail();
            _this.dispatchSlideStopEvent();
        };
        _this.handlePanningMove = function (event) {
            var axis = _this.option.axis;
            var offsetX = event.dx, offsetY = event.dy;
            var _a = _this.state, left = _a.left, right = _a.right, top = _a.top, bottom = _a.bottom, startPosition = _a.startPosition, endPosition = _a.endPosition, isPanning = _a.isPanning;
            if (!isPanning) {
                return;
            }
            var range = Math.abs(endPosition - startPosition);
            var nextStart;
            var nextEnd;
            if (axis === 'x') {
                nextStart = offsetX + startPosition;
                nextEnd = offsetX + endPosition;
                var minPosition = Math.min(nextStart, nextEnd);
                var maxPosition = Math.max(nextStart, nextEnd);
                if (offsetX < 0 && minPosition < left) {
                    nextStart = left;
                    nextEnd = left + range;
                }
                if (offsetX > 0 && maxPosition > right) {
                    nextStart = right - range;
                    nextEnd = right;
                }
            }
            else {
                nextStart = offsetY + startPosition;
                nextEnd = offsetY + endPosition;
                var minPosition = Math.min(nextStart, nextEnd);
                var maxPosition = Math.max(nextStart, nextEnd);
                if (offsetY < 0 && minPosition < top) {
                    nextStart = top;
                    nextEnd = top + range;
                }
                if (offsetY > 0 && maxPosition > bottom) {
                    nextStart = bottom - range;
                    nextEnd = bottom;
                }
            }
            _this.setPositionRange(nextStart, nextEnd);
        };
        return _this;
    }
    Slider.prototype.getDefaultOption = function () {
        return {
            show: false,
            position: 'bottom',
            align: 'center',
            axis: this.extraInfo.index === 0 ? 'x' : 'y',
            xAxis: [0],
            yAxis: [0],
            realTime: true,
            margin: 24,
            width: '100%',
            size: 28,
            start: null,
            end: null,
            lockRange: false,
            showDetail: true,
            handleIcon: "path://" + handleIconPath,
            textStyle: {
                color: '#4D5369',
                fontSize: 12,
            },
            fillerStyle: {
                stroke: 'rgba(0, 0, 0, 0)',
                lineWidth: 0,
                fill: '#CCD0E1',
                opacity: 0.5,
            },
            // handleSize: [7, 24],
            handleStyle: {
                stroke: 'none',
                lineWidth: 0,
                fill: '#AFB5CC',
            },
            backgroundStyle: {
                stroke: 'rgba(0, 0, 0, 0)',
                lineWidth: 0,
                fill: '#F0F2F6',
            },
            backgroundChart: {
                type: 'area',
                stroke: '#CCD0E1',
                lineWidth: 1,
                fill: '#CCD0E1',
                fillOpacity: 0.6,
            },
            throttle: 100,
        };
    };
    Slider.prototype.init = function () {
        var _this = this;
        this.initAxisRange(this.option.start, this.option.end);
        this.registerEvent('changedata', function () {
            var range = [
                _this._scale.invert(_this.state.startPosition),
                _this._scale.invert(_this.state.endPosition),
            ];
            var start = lodash.min(range);
            var end = lodash.max(range);
            if (_this.getSliderAxis().isCategroy() && _this.option.show) {
                _this.initAxisRange(start, end);
            }
        });
        this.applyPosition = lodash.throttle(this.applyPosition.bind(this), this.option.throttle);
        this._panningBar = new Rect({
            draggable: true,
            getDragOffset: function () {
                return { x: 0, y: 0 };
            },
            onDragStart: function () {
                if (_this.state.isZero) {
                    return;
                }
                _this.chart.setInteraction(true);
                _this.setState({ isPanning: true });
                _this.chart.dispatchAction({
                    type: 'sliderstart',
                });
            },
            onDrag: this.handlePanningMove,
            onDragEnd: function () {
                if (!_this.state.isPanning) {
                    return;
                }
                _this.chart.setInteraction(false);
                _this.setState({ isPanning: false });
                _this.dispatchSlideStopEvent();
                _this.renderDetail();
            },
        });
        this.initHandleIcons();
    };
    Slider.prototype.initHandleIcons = function () {
        var _a = this.option, handleIcon = _a.handleIcon, size = _a.size, _b = _a.handleSize, handleSize = _b === void 0 ? [7, size] : _b;
        var _c = getSymbolSize(handleSize), handleWidth = _c[0], handleHeight = _c[1];
        var startHandle = createSymbolAtCenter(handleIcon, 0, 0, handleWidth, handleHeight);
        var endHandle = createSymbolAtCenter(handleIcon, 0, 0, handleWidth, handleHeight);
        this._handleIcons = [startHandle, endHandle];
    };
    Slider.prototype.initAxisRange = function (start, end) {
        var sliderAxis = this.getSliderAxis();
        var extreme = sliderAxis.getSliderExtreme();
        var range = extreme[1] - extreme[0];
        var axisOption = sliderAxis.getOption();
        if (lodash.isNumber(start) || lodash.isString(start)) {
            axisOption.min = isPercent(start)
                ? axisOption.min + getPercentOrNumberValue(start, range)
                : start;
            if (start === '0%') {
                axisOption.min = null;
            }
        }
        if (lodash.isNumber(end) || lodash.isString(end)) {
            axisOption.max = isPercent(end) ? axisOption.min + getPercentOrNumberValue(end, range) : end;
            if (end === '100%') {
                axisOption.max = null;
            }
        }
    };
    Slider.prototype.getInitialState = function () {
        return {
            top: null,
            left: null,
            bottom: null,
            right: null,
            width: null,
            height: null,
            isDragging: false,
            draggingSide: null,
            isPanning: false,
            start: 0,
            end: 1,
            startPosition: null,
            endPosition: null,
            isZero: false,
        };
    };
    Slider.prototype.getSideAreaSize = function () {
        var side;
        if (this.option.axis === 'x') {
            if (this.option.position === 'top' || this.option.position === 'bottom') {
                side = this.option.position;
            }
            else {
                side = 'bottom';
            }
        }
        else if (this.option.axis === 'y') {
            if (this.option.position === 'left' || this.option.position === 'right') {
                side = this.option.position;
            }
            else {
                side = 'right';
            }
        }
        return {
            side: side,
            size: this.option.show ? this.option.size + this.option.margin : 0,
        };
    };
    Slider.prototype.beforeRender = function () {
        if (this.state.isPanning || this.state.isDragging) {
            return;
        }
        var align = this.option.align;
        var axis = this.option.axis;
        var grid = this.chart.getComponentModel('coord').grid;
        var sliderAxis = axis === 'x' ? grid.xAxis[0] : grid.yAxis[0];
        var inverse = sliderAxis.getOption().inverse;
        var _a = sliderAxis.getSliderExtreme(), min = _a[0], max = _a[1];
        var _b = sliderAxis.getExtreme(true), dMin = _b[0], dMax = _b[1];
        var isZero = dMax === dMin;
        var dataMin = isZero ? 0 : dMin;
        var dataMax = isZero ? 1 : dMax;
        var start = isZero ? 0 : lodash.clamp((min - dataMin) / (dataMax - dataMin), 0, 1);
        var end = isZero ? 1 : lodash.clamp((max - dataMin) / (dataMax - dataMin), 0, 1);
        var _c = grid.getState(), gridLeft = _c.left, gridRight = _c.right, gridTop = _c.top, gridBottom = _c.bottom, gridWidth = _c.width, gridHeight = _c.height;
        var sidePosition = this.chart.getSidePosition(this.id);
        var sliderWidth;
        var left;
        var right;
        var top;
        var bottom;
        var width;
        var height;
        var startPosition;
        var endPosition;
        if (axis === 'x') {
            sliderWidth = getPercentOrNumberValue(this.option.width, gridWidth);
            if (align === 'start') {
                left = gridLeft;
            }
            else if (align === 'center' || align === 'middle') {
                left = gridLeft + (gridWidth - sliderWidth) / 2;
            }
            else if (align === 'end') {
                left = gridRight - sliderWidth;
            }
            width = sliderWidth;
            height = this.option.size;
            right = left + sliderWidth;
            top = this.option.position === 'top' ? sidePosition : sidePosition - this.option.size;
            bottom = this.option.position === 'top' ? sidePosition + this.option.size : sidePosition;
            startPosition = left + sliderWidth * start;
            endPosition = left + sliderWidth * end;
            this._scale = !inverse
                ? new LinearScale(dataMin, dataMax, left, right)
                : new LinearScale(dataMin, dataMax, right, left);
        }
        else if (axis === 'y') {
            sliderWidth = getPercentOrNumberValue(this.option.width, gridHeight);
            if (align === 'start') {
                top = gridTop;
            }
            else if (align === 'center' || align === 'middle') {
                top = gridTop + (gridHeight - sliderWidth) / 2;
            }
            else if (align === 'end') {
                top = gridBottom - sliderWidth;
            }
            width = this.option.size;
            height = sliderWidth;
            left = this.option.position === 'left' ? sidePosition : sidePosition - this.option.size;
            right = this.option.position === 'left' ? sidePosition + this.option.size : sidePosition;
            bottom = top + sliderWidth;
            startPosition = inverse ? top + sliderWidth * start : bottom - sliderWidth * start;
            endPosition = inverse ? top + sliderWidth * end : bottom - sliderWidth * end;
            this._scale = !inverse
                ? new LinearScale(dataMin, dataMax, bottom, top)
                : new LinearScale(dataMin, dataMax, top, bottom);
        }
        this.setState({
            start: start,
            end: end,
            top: top,
            left: left,
            right: right,
            bottom: bottom,
            width: width,
            height: height,
            startPosition: startPosition,
            endPosition: endPosition,
            isZero: isZero,
        });
    };
    // eslint-disable-next-line no-unused-vars
    Slider.prototype.handleSideDragStart = function (side, event) {
        if (this.state.isZero) {
            return;
        }
        // this.chart.setInteraction(true);
        this.chart.dispatchAction({
            type: 'sliderstart',
        });
        this.setState({
            isDragging: true,
            draggingSide: side,
        });
    };
    Slider.prototype.handleSideDragMove = function (event) {
        var axis = this.option.axis;
        var dx = event.dx, dy = event.dy;
        var _a = this.state, left = _a.left, right = _a.right, top = _a.top, bottom = _a.bottom, startPosition = _a.startPosition, endPosition = _a.endPosition, draggingSide = _a.draggingSide, isDragging = _a.isDragging;
        if (!isDragging) {
            return;
        }
        if (draggingSide === 'start') {
            var nextPosition = axis === 'x'
                ? lodash.clamp(startPosition + dx, left, right)
                : lodash.clamp(startPosition + dy, top, bottom);
            this.setPositionRange(nextPosition, endPosition);
        }
        else if (draggingSide === 'end') {
            var nextPosition = axis === 'x'
                ? lodash.clamp(endPosition + dx, left, right)
                : lodash.clamp(endPosition + dy, top, bottom);
            this.setPositionRange(startPosition, nextPosition);
        }
    };
    Slider.prototype.setPositionRange = function (startPosition, endPosition) {
        if (this.option.axis === 'x') {
            this._panningBar.setAttr({
                x: startPosition,
                width: endPosition - startPosition,
            });
        }
        else {
            this._panningBar.setAttr({
                y: startPosition,
                height: endPosition - startPosition,
            });
        }
        this.setState({
            startPosition: startPosition,
            endPosition: endPosition,
        });
        var _a = this.getHandlePositionAttr(), startAttr = _a[0], endAttr = _a[1];
        this._startHandleRef.current.setAttr(startAttr);
        this._endHandleRef.current.setAttr(endAttr);
        this.renderDetail();
        this.applyPosition();
    };
    Slider.prototype.dispatchSlideStopEvent = function () {
        var _a = this.state, startPosition = _a.startPosition, endPosition = _a.endPosition;
        var range = [this._scale.invert(startPosition), this._scale.invert(endPosition)];
        var start = lodash.min(range);
        var end = lodash.max(range);
        var action = {
            type: 'sliderstop',
            start: start,
            end: end,
            sliderIndex: this.extraInfo.index,
        };
        this.chart.dispatchAction(action);
    };
    Slider.prototype.getSliderAxis = function () {
        var axis = this.option.axis;
        var grid = this.chart.getComponentModel('coord').grid;
        return axis === 'x' ? grid.xAxis[0] : grid.yAxis[0];
    };
    Slider.prototype.applyPosition = function () {
        var _this = this;
        var _a = this.state, startPosition = _a.startPosition, endPosition = _a.endPosition;
        var _b = this.option, axis = _b.axis, xAxis = _b.xAxis, yAxis = _b.yAxis;
        var range = [this._scale.invert(startPosition), this._scale.invert(endPosition)];
        var start = lodash.min(range);
        var end = lodash.max(range);
        this.setState({
            start: start,
            end: end,
        });
        var axisIndexList = axis === 'x' ? xAxis : yAxis;
        axisIndexList.forEach(function (index) {
            var axisModel = _this.chart.getComponentModel('coord').getAxis(axis, index);
            axisModel.getOption().min = start;
            axisModel.getOption().max = end;
        });
        this.chart.refresh();
    };
    Slider.prototype.getHandlePositionAttr = function () {
        var axis = this.option.axis;
        var _a = this.state, top = _a.top, left = _a.left, right = _a.right, bottom = _a.bottom, startPosition = _a.startPosition, endPosition = _a.endPosition;
        var startX;
        var startY;
        var endX;
        var endY;
        var rotation = 0;
        if (axis === 'x') {
            startY = endY = (top + bottom) / 2;
            startX = startPosition;
            endX = endPosition;
        }
        else if (axis === 'y') {
            rotation = Math.PI / 2;
            startX = endX = (left + right) / 2;
            startY = startPosition;
            endY = endPosition;
        }
        return [
            {
                translateX: startX,
                translateY: startY,
                rotation: rotation,
            },
            {
                translateX: endX,
                translateY: endY,
                rotation: rotation,
            },
        ];
    };
    Slider.prototype.renderHandles = function () {
        var _this = this;
        var _a = this.option, axis = _a.axis, lockRange = _a.lockRange;
        var _b = this._handleIcons, startHandle = _b[0], endHandle = _b[1];
        var _c = this.getHandlePositionAttr(), startAttr = _c[0], endAttr = _c[1];
        var cursor = lockRange ? 'not-allowed' : axis === 'x' ? 'ew-resize' : 'ns-resize';
        startHandle.setAttr(__assign(__assign(__assign({ key: this.getKey('start-handle'), ref: this._startHandleRef }, this.getShapeStyle(this.option.handleStyle)), startAttr), { draggable: !this.option.lockRange, getDragOffset: function () {
                return { x: 0, y: 0 };
            }, onDragStart: function (event) { return _this.handleSideDragStart('start', event); }, onDrag: function (event) { return _this.handleSideDragMove(event); }, onDragEnd: this.handleSideDragEnd, cursor: cursor }));
        endHandle.setAttr(__assign(__assign(__assign({ key: this.getKey('end-handle'), ref: this._endHandleRef }, this.getShapeStyle(this.option.handleStyle)), endAttr), { draggable: !this.option.lockRange, getDragOffset: function () {
                return { x: 0, y: 0 };
            }, onDragStart: function (event) { return _this.handleSideDragStart('end', event); }, onDrag: function (event) { return _this.handleSideDragMove(event); }, onDragEnd: this.handleSideDragEnd, cursor: cursor }));
        return [startHandle, endHandle];
    };
    Slider.prototype.renderSliderBackground = function () {
        var _a = this.getState(), top = _a.top, left = _a.left, width = _a.width, height = _a.height;
        return new Rect(__assign({ key: this.getKey('slider-background'), x: left, y: top, width: width,
            height: height, transitionProperty: 'none', r: this.option.backgroundStyle.borderRadius }, this.getShapeStyle(this.option.backgroundStyle)));
    };
    Slider.prototype.renderBackgroundChart = function () {
        var _this = this;
        var _a = this.option, axis = _a.axis, backgroundChart = _a.backgroundChart;
        var _b = this.state, left = _b.left, right = _b.right, top = _b.top, bottom = _b.bottom, width = _b.width, height = _b.height;
        var sliderWidth = axis === 'x' ? width : height;
        var categoryAxis = this.getSliderAxis();
        var axisOption = categoryAxis.getOption();
        var inverse = axisOption.inverse;
        var padding = 4;
        var axisSeries = categoryAxis.getAxisSeries(true);
        if (axisSeries.length === 0) {
            return;
        }
        var points = axisSeries[0].getState().points;
        var pointList;
        var gap = backgroundChart.type === 'bar' ? 0.5 : 0;
        if (this.option.axis === 'x') {
            var minX = lodash.min(points.map(function (point, dataIndex) { return dataIndex; }));
            var maxX = lodash.max(points.map(function (point, dataIndex) { return dataIndex; }));
            var minY = lodash.min(points.map(function (point) { return lodash.toNumber(point.value); }));
            var maxY = lodash.max(points.map(function (point) { return lodash.toNumber(point.value); }));
            var xscale_1 = new LinearScale(minX - gap, maxX + gap, left, right);
            var yScale_1 = new LinearScale(minY, maxY, bottom - padding, top + padding);
            pointList = points.map(function (point, dataIndex) {
                var x = xscale_1.scale(dataIndex);
                var y = yScale_1.scale(point.value);
                return { x: x, y: y };
            });
        }
        else {
            var minY = lodash.min(points.map(function (point, dataIndex) { return dataIndex; }));
            var maxY = lodash.max(points.map(function (point, dataIndex) { return dataIndex; }));
            var minX = lodash.min(points.map(function (point) { return lodash.toNumber(point.value); }));
            var maxX = lodash.max(points.map(function (point) { return lodash.toNumber(point.value); }));
            var yScale_2 = new LinearScale(minY - gap, maxY + gap, inverse ? top : bottom, inverse ? bottom : top);
            var xscale_2 = new LinearScale(minX, maxX, left + padding, right - padding);
            pointList = points.map(function (point, dataIndex) {
                var y = yScale_2.scale(dataIndex);
                var x = xscale_2.scale(point.value);
                return { x: x, y: y };
            });
        }
        var group = new Group({
            key: this.getKey('slider-background-chart'),
        });
        if (pointList.length === 0) {
            return group;
        }
        if (backgroundChart.type === 'area') {
            group.add(new Polyline({
                key: this.getKey('data-line'),
                pointList: pointList,
                lineWidth: backgroundChart.lineWidth,
                stroke: backgroundChart.stroke,
                transitionProperty: 'none',
                pointerEvents: 'none',
            }));
            var polyline = new Polyline({
                pointList: pointList,
            });
            var lastPoint = lodash.last(pointList);
            var firstPoint = lodash.first(pointList);
            var connectLine = void 0;
            if (axis === 'x') {
                connectLine = new Polyline({
                    pointList: [
                        {
                            x: lastPoint.x,
                            y: lastPoint.y,
                        },
                        {
                            x: lastPoint.x,
                            y: bottom,
                        },
                        {
                            x: firstPoint.x,
                            y: bottom,
                        },
                    ],
                });
            }
            else {
                connectLine = new Polyline({
                    pointList: [
                        {
                            x: lastPoint.x,
                            y: lastPoint.y,
                        },
                        {
                            x: left,
                            y: lastPoint.y,
                        },
                        {
                            x: left,
                            y: firstPoint.y,
                        },
                    ],
                });
            }
            var area = new CompoundPath({
                key: this.getKey('data-chart-area'),
                shapes: [polyline, connectLine],
                fill: backgroundChart.fill,
                fillOpacity: backgroundChart.fillOpacity,
                transitionProperty: 'none',
                pointerEvents: 'none',
            });
            group.add(area);
        }
        else if (backgroundChart.type === 'bar') {
            var barWidth_1 = sliderWidth / pointList.length;
            var barGroup_1 = new Group({
                key: this.getKey('chart-bar'),
            });
            pointList.forEach(function (point) {
                var x = point.x, y = point.y;
                if (axis === 'x') {
                    barGroup_1.add(new Rect(__assign(__assign({ x: x - barWidth_1 / 2, y: y, width: barWidth_1, height: bottom - y }, _this.getShapeStyle(_this.option.backgroundChart)), { transitionProperty: 'none' })));
                }
                else {
                    barGroup_1.add(new Rect(__assign(__assign({ x: left, y: y - barWidth_1 / 2, width: x - left, height: barWidth_1 }, _this.getShapeStyle(_this.option.backgroundChart)), { transitionProperty: 'none' })));
                }
            });
            group.add(barGroup_1);
        }
        return group;
    };
    /**
     * filler
     */
    Slider.prototype.renderFiller = function () {
        var axis = this.option.axis;
        var _a = this.state, startPosition = _a.startPosition, endPosition = _a.endPosition, top = _a.top, left = _a.left, height = _a.height, width = _a.width;
        if (axis === 'x') {
            this._panningBar.setAttr(__assign(__assign({ key: this.getKey('panning-bar'), x: Math.min(startPosition, endPosition), y: top, width: Math.abs(startPosition - endPosition), height: height }, this.getShapeStyle(this.option.fillerStyle)), { r: this.option.fillerStyle.borderRadius, cursor: 'move', transitionProperty: 'none' }));
        }
        else if (axis === 'y') {
            this._panningBar.setAttr(__assign(__assign({ key: this.getKey('panning-bar'), x: left, y: Math.min(startPosition, endPosition), width: width, height: Math.abs(startPosition - endPosition) }, this.getShapeStyle(this.option.fillerStyle)), { r: this.option.fillerStyle.borderRadius, cursor: 'move', transitionProperty: 'none' }));
        }
        return this._panningBar;
    };
    Slider.prototype.renderDetail = function () {
        var _a = this.state, startPosition = _a.startPosition, endPosition = _a.endPosition, top = _a.top, left = _a.left, right = _a.right, bottom = _a.bottom;
        var startSide = Math.min(startPosition, endPosition);
        var endSide = Math.max(startPosition, endPosition);
        var categoryAxis = this.getSliderAxis();
        var categories = categoryAxis.getCategories();
        var startValue = Math.round(this._scale.invert(startSide));
        var endValue = Math.round(this._scale.invert(endSide));
        var p1 = { x: 0, y: 0 };
        var p2 = { x: 0, y: 0 };
        var margin = 9;
        var startText = categories[startValue];
        var endText = categories[endValue];
        var startTextWidth = measureText(startText, this.option.textStyle).width;
        var endTextWidth = measureText(endText, this.option.textStyle).width;
        if (this.option.axis === 'x') {
            var startDistance = startPosition - left;
            var endDistance = right - endPosition;
            p1.y = p2.y = (top + bottom) / 2;
            p1.x = startSide - margin;
            p2.x = endSide + margin;
            p1.textAlign = 'right';
            p2.textAlign = 'left';
            p1.textBaseline = p2.textBaseline = 'middle';
            if (startDistance < startTextWidth) {
                p1.x = startSide + margin;
                p1.textAlign = 'left';
            }
            if (endDistance < endTextWidth) {
                p2.x = endSide - margin;
                p2.textAlign = 'right';
            }
        }
        else if (this.option.axis === 'y') {
            p1.x = p2.x = (left + right) / 2;
            p1.y = startSide - margin;
            p2.y = endSide + margin;
            p1.textBaseline = 'bottom';
            p2.textBaseline = 'top';
            p1.textAlign = p2.textAlign = 'center';
        }
        var show = this.state.isDragging || this.state.isPanning;
        this._detailText[0].setAttr(__assign(__assign(__assign({ display: show, text: startText }, p1), this.getTextStyle(this.option.textStyle)), { pointerEvents: 'none' }));
        this._detailText[1].setAttr(__assign(__assign(__assign({ display: show, text: endText }, p2), this.getTextStyle(this.option.textStyle)), { pointerEvents: 'none' }));
        return this._detailText;
    };
    Slider.prototype.updated = function () {
        this.setState({ isDragging: false, isPanning: false });
        this.initAxisRange(this.option.start, this.option.end);
        if (this.chart.getRenderInstance().renderer !== 'svg') {
            this.initHandleIcons();
        }
    };
    Slider.prototype.render = function () {
        if (!this.option.show) {
            return null;
        }
        if (this.state.isDragging || this.state.isPanning) {
            return this.renderObj;
        }
        var group = new Group({
            display: this.option.show,
            key: this.getKey('slider'),
        });
        group.add(this.renderSliderBackground());
        group.add(this.renderBackgroundChart());
        group.add(this.renderFiller());
        group.addAll(this.renderHandles());
        if (this.option.showDetail) {
            group.addAll(this.renderDetail());
        }
        return group;
    };
    Slider.multiple = true;
    return Slider;
}(ComponentModel));
export default Slider;
registerComponent('slider', Slider);
